import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { usePrevious } from '../../../hooks/usePrevious';
import {
    AgGridEvent,
    ClientSideRowModelModule,
    ColumnResizedEvent,
    FilterChangedEvent,
    GridApi,
    GridReadyEvent,
    SelectionChangedEvent,
} from 'ag-grid-community';
import { Client } from '../../../../core/domainModels/clients/Client';
import { clientsTableColumnsDefinitions, clientsTableDefaultColumnsDefs } from './models/columnDefinitions';
import { currentTranslations } from '../../../../core/locales/locale';
import { LicenceVersionDetails } from '../../licenses/components/LicenceVersionDetails';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../state/ApplicationState';
import { TachoModal } from '../../../components/TachoModal';
import { TachoEditModalTitle } from '../../../components/TachoModal/TachoEditTitle';
import { Icon } from 'tacho-ui';
import { TableButtonRenderer } from '../../../components/AgGrid/Renderers/TableRemoveButton/TableRemoveButton';
import {
    applyInitialSortFromDisplayPreferences,
    createTachoColumnState,
} from '../../../../core/domainModels/displayPreferences/tachoColumnState';
import { modifyClientsDisplayPreferences, modifyClientsTableFilters } from '../../../../state/clients/reducer';
import AgGridContextMenuWrapper from '../../../components/ContextMenu/AgGridContextMenuWrapper';

interface ClientsTableProps {
    clients: Array<Client>;
    onClientDoubleClicked: () => void;
    onSelectionChanged: (clients: Array<Client>) => void;
}
export const ClientsTable = (props: ClientsTableProps) => {
    const dispatch = useDispatch();
    const displayPreferences = useSelector((state: ApplicationState) => state.clients.displayPreferences);
    const tableFiltersState = useSelector((state: ApplicationState) => state.clients.tableFilters);
    const { formatMessage } = useIntl();
    const gridApi = useRef<GridApi>();
    const [detailsModalData, setDetailsModalData] = useState<{ modalOpen: boolean; data?: Client }>({
        modalOpen: false,
        data: undefined,
    });
    const licences = useSelector((state: ApplicationState) => state.licences.licences);

    const openLicencesModal = useCallback((isModalOpen: boolean, data?: Client) => {
        setDetailsModalData({ modalOpen: isModalOpen, data: data });
    }, []);

    const columnDefs = useMemo(
        () => clientsTableColumnsDefinitions(formatMessage, openLicencesModal, displayPreferences),
        [formatMessage, displayPreferences, openLicencesModal],
    );

    const previousClients = usePrevious(props.clients);
    useEffect(() => {
        const added = props.clients.filter(client => !previousClients?.find(prevClient => prevClient.id === client.id));
        const removed = previousClients?.filter(prevClient => !props.clients?.find(client => prevClient.id === client.id));
        const updated = props.clients.filter(client => !added.some(addedClient => addedClient.id === client.id));
        gridApi.current?.applyTransaction({
            add: added,
            remove: removed,
            update: updated,
        });
    }, [props.clients]);

    const rowsSelectionChanged = (event: SelectionChangedEvent) => {
        const selectedNodes = event.api.getSelectedNodes()?.map(node => node.data as Client);
        if (!selectedNodes) {
            console.log('No selected rows.');
            return;
        }

        props.onSelectionChanged(selectedNodes);
    };

    const onGridReady = useCallback(
        (params: GridReadyEvent) => {
            gridApi.current = params.api;
            applyInitialSortFromDisplayPreferences(params, displayPreferences, [{ colId: 'name', sort: 'asc' }]);
            params.api.setFilterModel(tableFiltersState);
            params.api.setGridOption('rowData', props.clients);
        },
        [props.clients, displayPreferences, tableFiltersState],
    );

    const sideBarConfiguration = {
        toolPanels: [
            {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
                toolPanelParams: {
                    suppressRowGroups: true,
                    suppressValues: true,
                    suppressPivotMode: true,
                    suppressColumnFilter: true,
                    suppressColumnSelectAll: true,
                },
            },
        ],
    };

    useEffect(() => {
        gridApi.current?.refreshCells();
    }, [licences]);

    function onColumnsChanged(event: AgGridEvent): void {
        const tachoColumnState = createTachoColumnState(event.api.getColumnState());
        dispatch(modifyClientsDisplayPreferences(tachoColumnState));
    }
    const onColumnResized = (event: ColumnResizedEvent) => {
        if (event.finished) {
            return onColumnsChanged(event);
        }
    };
    const onTableFiltersStateChanged = (event: FilterChangedEvent) => {
        const filters = event.api.getFilterModel();
        dispatch(modifyClientsTableFilters(filters));
    };

    return (
        <>
            <AgGridContextMenuWrapper gridApi={gridApi.current} showStatusBar>
                <AgGridReact
                    suppressPropertyNamesCheck
                    getRowId={params => params.data?.id ?? ''}
                    suppressDragLeaveHidesColumns
                    suppressContextMenu={true}
                    multiSortKey="ctrl"
                    rowSelection="multiple"
                    defaultColDef={clientsTableDefaultColumnsDefs}
                    columnDefs={columnDefs}
                    modules={[ClientSideRowModelModule]}
                    onGridReady={onGridReady}
                    onSelectionChanged={rowsSelectionChanged}
                    suppressScrollOnNewData={true}
                    sideBar={sideBarConfiguration}
                    localeText={currentTranslations?.values}
                    onRowDoubleClicked={e => {
                        props.onClientDoubleClicked();
                    }}
                    components={{ tableButtonRenderer: TableButtonRenderer }}
                    onSortChanged={event => onColumnsChanged(event)}
                    onColumnMoved={event => onColumnsChanged(event)}
                    onColumnResized={event => onColumnResized(event)}
                    onFilterChanged={event => onTableFiltersStateChanged(event)}
                />
            </AgGridContextMenuWrapper>
            <>
                {detailsModalData.modalOpen && (
                    <TachoModal
                        height="auto"
                        isOpen={!!detailsModalData}
                        title={<TachoEditModalTitle icon={Icon.Licenses} titleToTranslation="licencesAddButton" />}
                        hideSubmitButton
                        cancelLabelToTranslate="tachoModalClose"
                        onToggle={() => setDetailsModalData({ data: undefined, modalOpen: false })}
                    >
                        <LicenceVersionDetails
                            data={detailsModalData.data}
                            licenceData={licences.filter(item => item.clientId === detailsModalData.data?.id)}
                            formatMessage={formatMessage}
                        />
                    </TachoModal>
                )}
            </>
        </>
    );
};
